import i18n from '../i18n';
import api from './index';
import headersToObject from './utils/headersToObject';

/**
 * Create requests to fetch or send resources to the server using the `Fetch` API.
 *
 * IMPORTANT NOTE:
 * For file uploads use the `_xhr.js` which utilizes the `XMLHttpRequest (XHR)` object and allows to abort requests and track upload progress.
 * Use this one for any other type of requests.
 *
 * @param {String} url API endpoint
 * @param {Object} [userOptions={}]
 * @param {Boolean} [userOptions.withAuth] Denotes if should add auth header or not.
 * @param {String} [userOptions.responseType="json"] Rsponse type; tha way to handle the response. Avialbale values: `arrayBuffer`, `blob`, `json`, `text`, `formData`.
 * @param {Object} fetchConfig Required cinfiguration for the `Fetch` API; eg `fetchConfig.method`, `fetchConfig.body`, etc...
 */
function _fetch(url, userOptions = {}, fetchConfig = {}) {
  return function withcontext(context) {
    const defaultUserOptions = {
      withAuth: true,
      withBasicAuth: false,   // Bearer {token}
      responseType: 'json'    // Basic {token}
    };

    userOptions = {
      ...defaultUserOptions,
      ...userOptions
    };

    fetchConfig.headers = fetchConfig.headers || {};

    if (userOptions.withAuth) {
      fetchConfig.headers.Authorization = `Bearer ${context._token}`;
    }

    if (i18n.language) {
      fetchConfig.headers['Accept-Language'] = i18n.language;
    }

    return fetch(url, fetchConfig).then(response => {
      const defaultErrorDescription = 'An error occurred with the server.';
      let returnable;

      if (response.ok) {
        returnable = response[userOptions.responseType || 'json']().then( responseContents => {
          let promiseResult;
          switch (userOptions.responseType) {
            case 'blob':
              promiseResult = {
                blob: responseContents,
                headers: headersToObject(response.headers)
              };
              break;
            default:
              promiseResult = responseContents;
              break;
          }
          return Promise.resolve(promiseResult);
        }).catch(() => {});
      } else {
        if (response.status === 401 && userOptions.withAuth) {
          api.reset();
          return Promise.reject();
        }

        returnable = response.json().then(json => {
          return Promise.reject({
            error: true,
            status: response.status,
            code: json.code || response.status,
            description: json.description || json.message || defaultErrorDescription,
            details: json.details,
            metadata: json.metadata
          });
        }, error => {
          return Promise.reject({
            error,
            status: response.status,
            code: response.status,
            description: response.description || response.message || defaultErrorDescription,
            details: response.details,
            metadata: {}
          });
        });
      }

      return returnable;
    }).catch(err => Promise.reject(err));
  };
}

export default _fetch;
